import { calculateAge } from '@/utils/calculateAge';
import { differenceInDays } from 'date-fns';
import { z } from 'zod';

/** Mirrors proposerSchema.superRefine (Aadhaar OR PAN + age) */
export const proposerDescriptorSuperRefine = (
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
) => {
  const dateOfBirth = data.dateOfBirth as string | undefined;
  if (dateOfBirth && calculateAge(dateOfBirth) < 18) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Proposer must be at least 18 years old',
      path: ['dateOfBirth'],
    });
  }

  const aadharNumber = data.aadharNumber as string | undefined;
  const panNumber = data.panNumber as string | undefined;
  const hasAadhar = !!aadharNumber?.trim();
  const hasPan = !!panNumber?.trim();

  if (!hasAadhar && !hasPan) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Aadhar number is required',
      path: ['aadharNumber'],
    });
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'PAN number is required',
      path: ['panNumber'],
    });
    return;
  }

  if (hasAadhar) {
    const aadharRegex = /^\d{12}$/;
    if (!aadharRegex.test(aadharNumber!.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Aadhaar number must contain exactly 12 digits',
        path: ['aadharNumber'],
      });
    }
  }

  if (hasPan) {
    const panRegex = /^[A-Z]{5}\d{4}[A-Z]$/;
    const normalizedPan = panNumber!.trim().toUpperCase();
    if (!panRegex.test(normalizedPan)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid PAN number',
        path: ['panNumber'],
      });
    }
  }
};

/** Mirrors nomineeSchema date/relationship rules */
export const nomineeDescriptorSuperRefine = (
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
) => {
  const dob = data.dateOfBirth as string;
  if (!dob) return;

  if (new Date(dob) > new Date()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Nominee date of birth cannot be in the future',
      path: ['dateOfBirth'],
    });
  }

  const age = calculateAge(dob);
  if (age < 18) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Nominee must be at least 18 years old',
      path: ['dateOfBirth'],
    });
  }
  if (age > 74) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Nominee must be at most 74 years old',
      path: ['dateOfBirth'],
    });
  }
};

/** Mirrors memberFormSchema.superRefine for age by relation */
export const memberPersonalDescriptorSuperRefine = (
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
) => {
  const dob = data.dateOfBirth as string;
  const relation = (data.relation as string) ?? '';
  if (!dob) return;

  const age = calculateAge(dob);
  const ageInDays = differenceInDays(new Date(), new Date(dob));

  if (relation.includes('CHILD')) {
    if (ageInDays < 91) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Child must be at least 91 days old',
        path: ['dateOfBirth'],
      });
    } else if (age >= 30) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Child must be under 30 years old',
        path: ['dateOfBirth'],
      });
    }
  } else if (age < 18) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Must be at least 18 years old',
      path: ['dateOfBirth'],
    });
  }
};

/** Mirrors bankDetailsSchema.superRefine */
export const bankDetailsDescriptorSuperRefine = (
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
) => {
  const bankAccountNumber = (data.bankAccountNumber as string)?.trim() ?? '';
  const confirm =
    (data.confirmBankAccountNumber as string)?.trim() ?? '';
  if (bankAccountNumber !== confirm) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Account numbers do not match',
      path: ['confirmBankAccountNumber'],
    });
  }
};
